/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */

// Maps the different error shapes of the backend (ProblemDetails, GeneralError, IdentityError[], validation errors) to one message.
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const anyErr = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };

  const status = anyErr?.response?.status;
  const data = anyErr?.response?.data;

  if (status !== undefined && status >= 500) {
    return 'The server is currently unavailable. Please try again later.';
  }
  if (status === 401) return 'You do not have permission to do this, or your session has expired.';
  if (status === 403) return 'You do not have permission to do this.';

  const fromData = messageFromData(data);
  if (fromData) return fromData;

  switch (status) {
    case 400:
      return 'The entered data is not valid.';
    case 404:
      return 'The requested entry was not found.';
    case 409:
      return 'This entry already exists.';
    default:
      break;
  }

  if (!anyErr?.response && anyErr?.message) {
    return 'The server could not be reached. Please check your connection.';
  }

  return fallback;
}

function messageFromData(data: unknown): string | undefined {
  if (data === null || data === undefined) return undefined;

  if (typeof data === 'string') {
    const text = data.trim();
    const looksTechnical = /exception|stack trace|\n\s+at\s|<html/i.test(text);
    return text.length > 0 && text.length < 300 && !looksTechnical ? text : undefined;
  }

  if (Array.isArray(data)) {
    const descriptions = data
      .map((item) => (item && typeof item === 'object' ? (item as { Description?: string }).Description : undefined))
      .filter((d): d is string => typeof d === 'string' && d.length > 0);
    return descriptions.length > 0 ? descriptions.join(' ') : undefined;
  }

  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    if (typeof obj.Error === 'string' && obj.Error) return obj.Error;
    if (typeof obj.detail === 'string' && obj.detail) return obj.detail;

    if (obj.errors && typeof obj.errors === 'object') {
      const messages = Object.values(obj.errors as Record<string, unknown>)
        .flat()
        .filter((m): m is string => typeof m === 'string');
      if (messages.length > 0) return messages.join(' ');
    }

    if (typeof obj.title === 'string' && obj.title) return obj.title;
  }

  return undefined;
}
